import React from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import type { SxProps, Theme } from "@mui/material/styles";

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  onBack?: () => void;
  backAriaLabel?: string;
  sx?: SxProps<Theme>;
}

/**
 * Shared page header with consistent typography and spacing.
 * Use `actions` for right-aligned controls and `children` for helper rows below the title.
 */
export default function PageHeader({
  title,
  subtitle,
  actions,
  children,
  onBack,
  backAriaLabel = "Back",
  sx,
}: PageHeaderProps) {
  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ gap: { sm: 2 } }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {onBack && (
            <IconButton onClick={onBack} aria-label={backAriaLabel} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: subtitle ? 0.5 : 0 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {actions && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {actions}
          </Box>
        )}
      </Stack>

      {children && <Box sx={{ mt: 1 }}>{children}</Box>}
    </Box>
  );
}
